import { Box, Button, Flex, Heading, Input, InputGroup, SimpleGrid, Text } from '@chakra-ui/react';
import { UseFormReturn, useController } from 'react-hook-form';
import { useDebouncedFieldRegistry } from '@/utils/filterUtils/useDebouncedFieldRegistry';
import { ChakraFeatherIcon } from 'src/components/ChakraFeatherIcon';
import { SelectField } from 'src/components/SelectField';
import { Filters } from '../../types';
import { formatDateToDateInputValue } from '@/utils/formatDateToDateInputValue';
import { InputField } from '@/components/InputField';
import { GetParamsObject } from '@/utils';
import { usePartners } from '@/services/queries/partner';
import { useTranslation } from 'react-i18next';
import { Club } from '@/model';
import TeamFilterSelect from '@/components/TeamFilterSelect';

export interface StoreFilterBoxProps {
  filtersForm: UseFormReturn<Filters>;
  clubs: Club[];
}

export const StoreFilterBox = ({ clubs, filtersForm }: StoreFilterBoxProps) => {
  const { register, reset, control } = filtersForm;

  const { t } = useTranslation();

  const { data } = usePartners(GetParamsObject({}, 1, 900, 'asc', 'name'));
  const partners = data?.data || [];

  const startDateControl = useController({
    name: 'start_date',
    control,
  });
  const endDateControl = useController({
    name: 'end_date',
    control,
  });

  return (
    <Box>
      <Flex align='center' gap='pico' mb='deka'>
        <ChakraFeatherIcon color='text.main' name='filter' />
        <Heading variant='sectionTitle'>{t('pages.application_banner.components.filter_box.title')}</Heading>
      </Flex>

      <Flex direction={{ base: 'row' }} gap='small' rowGap='micro'>
        <SimpleGrid
          gap='deka'
          alignItems='flex-end'
          columns={{ base: 1, sm: 2, md: 4, lg: 5, xl: 6 }}
          rowGap='xxsmall'
          flexGrow={1}
        >
          <TeamFilterSelect showFlag name='team_uuid' control={filtersForm.control} clubs={clubs} width='100%' />

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>{t('pages.application_banner.components.filter_box.title_label')}</Text>
            <Input variant='flushed' {...useDebouncedFieldRegistry(() => register('title'))} />
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>{t('pages.application_banner.components.filter_box.partner')}</Text>
            <SelectField {...register('partner_uuid')}>
              <option value=''> {t('pages.application_banner.components.filter_box.all_partners')}</option>
              {partners?.map(partner => (
                <option key={partner.uuid} value={partner.uuid}>
                  {partner.name}
                </option>
              ))}
            </SelectField>
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>{t('pages.application_banner.components.filter_box.start_date')}:</Text>
            <InputField
              type='date'
              fullDatePickerArea
              variant='flushed'
              value={formatDateToDateInputValue(startDateControl.field.value)}
              onChange={event => startDateControl.field.onChange(event.target.value)}
            />
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>{t('pages.application_banner.components.filter_box.end_date')}</Text>
            <InputField
              type='date'
              fullDatePickerArea
              variant='flushed'
              value={formatDateToDateInputValue(endDateControl.field.value)}
              onChange={event => endDateControl.field.onChange(event.target.value)}
              min={formatDateToDateInputValue(startDateControl.field.value)}
            />
          </InputGroup>
        </SimpleGrid>

        <Button alignSelf={{ lg: 'end' }} colorScheme='text' variant='outline' mt='10px' onClick={() => reset()}>
          {t('pages.application_banner.components.filter_box.clear')}
        </Button>
      </Flex>
    </Box>
  );
};
